import { useEffect, useMemo, useState } from "react";

import {
  deleteMessageById,
  getMessagesByUser,
  getSentMessagesByUser,
  markAllMessagesAsRead,
  markMessageAsRead,
  sendMessage,
} from "../../../../services/messageServices";

import { getGroupsByUser, getGroupAdherents } from "../../../../services/groupServices";

import {
  MESSAGE_TABS,
  normalizeMessagesResponse,
} from "../utils/messagerieUtils";

export function useMessagerieJudoka({
  user,
  addNotification,
  setUnreadMessageCount,
}) {
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [approvedAdherents, setApprovedAdherents] = useState([]);

  const [activeTab, setActiveTab] = useState(MESSAGE_TABS.received);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [composeMode, setComposeMode] = useState("new");
  const [messageType, setMessageType] = useState("admin");

  const [messageSubject, setMessageSubject] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [replyRecipientId, setReplyRecipientId] = useState(null);

  const unreadCount = useMemo(
    () => receivedMessages.filter((message) => !message.read_message).length,
    [receivedMessages]
  );

  const currentMessages = useMemo(() => {
    return activeTab === MESSAGE_TABS.received ? receivedMessages : sentMessages;
  }, [activeTab, receivedMessages, sentMessages]);

  const fetchMessages = async () => {
    if (!user?.userId) {
      addNotification("Erreur : utilisateur non trouvé.", "error");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [receivedResponse, sentResponse] = await Promise.all([
        getMessagesByUser(user.userId),
        getSentMessagesByUser(user.userId),
      ]);

      const received = normalizeMessagesResponse(receivedResponse);
      const sent = normalizeMessagesResponse(sentResponse);

      setReceivedMessages(received);
      setSentMessages(sent);

      const nextUnreadCount = received.filter(
        (message) => !message.read_message
      ).length;

      setUnreadMessageCount(nextUnreadCount);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages :", error);
      addNotification("Erreur lors de la récupération des messages.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdherents = async () => {
    try {
      if (!user?.userId) return;

      const userGroups = await getGroupsByUser(user.userId);
      const adherents = [];

      for (const group of userGroups || []) {
        const response = await getGroupAdherents(group.id);
        const groupAdherents = response?.adherents || response || [];

        const approved = groupAdherents.filter(
          (adherent) => adherent.status === "approved"
        );

        adherents.push(...approved.map((adherent) => ({ ...adherent, group })));
      }

      setApprovedAdherents(adherents);
    } catch (error) {
      console.error("Erreur lors de la récupération des adhérents :", error);
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchAdherents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const openComposeModal = (type = "admin") => {
    setComposeMode("new");
    setMessageType(type);
    setReplyRecipientId(null);
    setMessageSubject("");
    setMessageContent("");
    setComposeModalOpen(true);
  };

  const openReplyModal = (message) => {
    setComposeMode("reply");
    setMessageType("reply");
    setReplyRecipientId(message.sender_id);
    setMessageSubject(
      message.subject?.startsWith("Re:") ? message.subject : `Re: ${message.subject || "Sans sujet"}`
    );
    setMessageContent("");
    setComposeModalOpen(true);
  };

  const closeComposeModal = () => {
    setComposeModalOpen(false);
    setComposeMode("new");
    setMessageType("admin");
    setReplyRecipientId(null);
    setMessageSubject("");
    setMessageContent("");
  };

  const handleSendMessage = async () => {
    if (!messageSubject.trim() || !messageContent.trim()) {
      addNotification("Le sujet et le contenu du message sont requis.", "error");
      return;
    }

    if (!user?.userId) {
      addNotification("Utilisateur introuvable.", "error");
      return;
    }

    setSending(true);

    try {
      if (composeMode === "reply") {
        await sendMessage({
          sender_id: user.userId,
          recipient_id: replyRecipientId,
          subject: messageSubject,
          content: messageContent,
        });
      } else {
        let recipientIds = [];

        if (messageType === "admin") {
          recipientIds = [1];
        } else if (messageType === "judokas") {
          recipientIds = approvedAdherents
            .map((adherent) => adherent.user_id || adherent.User?.id || adherent.id)
            .filter(Boolean);
        }

        await Promise.all(
          recipientIds.map((recipientId) =>
            sendMessage({
              sender_id: user.userId,
              recipient_id: recipientId,
              subject: messageSubject,
              content: messageContent,
            })
          )
        );
      }

      addNotification(
        composeMode === "reply"
          ? "Réponse envoyée avec succès."
          : "Message envoyé avec succès.",
        "success"
      );

      closeComposeModal();
      await fetchMessages();
    } catch (error) {
      console.error("Erreur lors de l'envoi du message :", error);
      addNotification("Erreur lors de l'envoi du message.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleToggleRead = async (message) => {
    try {
      const previousReadStatus = message.read_message;

      setReceivedMessages((prevMessages) =>
        prevMessages.map((item) =>
          item.id === message.id
            ? { ...item, read_message: !item.read_message }
            : item
        )
      );

      setUnreadMessageCount((currentCount) =>
        previousReadStatus ? currentCount + 1 : Math.max(0, currentCount - 1)
      );

      await markMessageAsRead(message.id);
    } catch (error) {
      console.error("Erreur lors du basculement de l'état de lecture :", error);
      addNotification(
        "Erreur lors du basculement de l'état de lecture.",
        "error"
      );
      await fetchMessages();
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.userId) return;

    try {
      await markAllMessagesAsRead(user.userId);

      setReceivedMessages((prevMessages) =>
        prevMessages.map((message) => ({
          ...message,
          read_message: true,
        }))
      );

      setUnreadMessageCount(0);
      addNotification("Tous les messages ont été marqués comme lus.", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de tous les messages:", error);
      addNotification("Erreur lors de la mise à jour des messages.", "error");
    }
  };

  const handleDeleteMessage = async (message) => {
    try {
      await deleteMessageById(message.id);

      if (activeTab === MESSAGE_TABS.received) {
        setReceivedMessages((prevMessages) =>
          prevMessages.filter((item) => item.id !== message.id)
        );

        if (!message.read_message) {
          setUnreadMessageCount((currentCount) => Math.max(0, currentCount - 1));
        }
      } else {
        setSentMessages((prevMessages) =>
          prevMessages.filter((item) => item.id !== message.id)
        );
      }

      addNotification("Message supprimé avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression du message :", error);
      addNotification("Erreur lors de la suppression du message.", "error");
    }
  };

  return {
    receivedMessages,
    sentMessages,
    currentMessages,
    activeTab,
    unreadCount,
    loading,
    sending,

    composeModalOpen,
    composeMode,
    messageSubject,
    messageContent,

    setActiveTab,
    setMessageSubject,
    setMessageContent,

    openComposeModal,
    openReplyModal,
    closeComposeModal,

    handleSendMessage,
    handleToggleRead,
    handleMarkAllAsRead,
    handleDeleteMessage,
  };
}